import React, { useMemo, useState } from 'react'
import { Button, Col, Dropdown, Form, InputGroup, Row } from 'react-bootstrap'

import DashboardTable from '../DashboardTable.component'
import { scoreCardTables } from '../DashboardMockData'
import { formatNumber, formatValueByUnit } from '@utils/index'
import { DashboardUnits } from '@dataTypes/dashboard.types'

import '../DashboardStyle.css'

// Extract constants outside of the component.
const ACTION_ITEMS = [
  { key: 'edit', title: 'Edit' },
  { key: 'duplicate', title: 'Duplicate' },
  { key: 'delete', title: 'Delete' },
]

function LocationTab(): React.ReactElement {
  const [selectedLoc, setSelectedLoc] = useState('Select all')
  const [data] = useState(scoreCardTables)

  const filteredData = useMemo(() => {
    return selectedLoc === 'Select all'
      ? data
      : data.filter((item) => item.location === selectedLoc)
  }, [data, selectedLoc])

  const locationOptions = useMemo(() => {
    const options = Array.from(new Set(data.map((item) => item.location)))
    return ['Select all', ...options]
  }, [data])

  const footerTemplate = (): React.ReactElement => <div>Total</div>

  const totalFooterTemplate = (
    props: any,
    sign: DashboardUnits,
  ): React.ReactElement => {
    const formattedSum = formatValueByUnit(props.Sum ?? 0, sign)

    if (sign === '$') {
      const numericPart = parseFloat(formattedSum.replace(/[^\d.-]/g, ''))
      const formattedNumericPart = numericPart.toFixed(2)
      return <div>{`$${formattedNumericPart}`}</div>
    }

    return <div>{formattedSum}</div>
  }

  const currencyTemplate = (
    dataSource: any,
    field: string,
  ): React.ReactElement => {
    const formatValue = parseFloat(
      formatNumber(dataSource[field], false),
    ).toFixed(2)
    return <div>${formatValue}</div>
  }

  const percentTemplate = (
    dataSource: any,
    field: string,
  ): React.ReactElement => {
    const formatValue = formatNumber(dataSource[field], false)
    return <div>{formatValue}%</div>
  }

  const columns = [
    {
      field: 'location',
      header: 'Location',
    },
    {
      field: 'cancelledAppt',
      header: 'Cancelled Appointment',
    },
    {
      field: 'newPatSeen',
      header: 'New Patient Seen',
    },
    {
      field: 'noShowAppt',
      header: 'No Show Appointment',
    },
    {
      field: 'appliedPayments',
      header: 'Applied Appointments',
      template: (datum: any) => currencyTemplate(datum, 'appliedPayments'),
    },
    {
      field: 'OTCRate',
      header: 'OTC Rate',
      template: (datum: any) => percentTemplate(datum, 'OTCRate'),
    },
    {
      field: 'recTXSched',
      header: 'Recommended Tx Schedule',
      template: (datum: any) => currencyTemplate(datum, 'recTXSched'),
    },
    {
      field: 'schedProd',
      header: 'Scheduled Production',
      template: (datum: any) => currencyTemplate(datum, 'schedProd'),
    },
    {
      field: 'reSchedAppt',
      header: 'Rescheduled Appt',
      template: (datum: any) => percentTemplate(datum, 'reSchedAppt'),
    },
    {
      field: 'rejectRation',
      header: 'Rejection Ratio',
      template: (datum: any) => currencyTemplate(datum, 'rejectRation'),
    },
  ]

  const footerColumns = [
    {
      field: 'location',
      template: footerTemplate,
    },
    {
      field: 'cancelledAppt',
      template: (datum: any) => totalFooterTemplate(datum, '#'),
    },
    {
      field: 'newPatSeen',
      template: (datum: any) => totalFooterTemplate(datum, '#'),
    },
    {
      field: 'noShowAppt',
      template: (datum: any) => totalFooterTemplate(datum, '#'),
    },
    {
      field: 'appliedPayments',
      template: (datum: any) => totalFooterTemplate(datum, '$'),
    },
    {
      field: 'OTCRate',
      template: (datum: any) => totalFooterTemplate(datum, '%'),
    },
    {
      field: 'recTXSched',
      template: (datum: any) => totalFooterTemplate(datum, '$'),
    },
    {
      field: 'schedProd',
      template: (datum: any) => totalFooterTemplate(datum, '$'),
    },
    {
      field: 'reSchedAppt',
      template: (datum: any) => totalFooterTemplate(datum, '%'),
    },
    {
      field: 'rejectRation',
      template: (datum: any) => totalFooterTemplate(datum, '$'),
    },
  ]

  return (
    <>
      <Row className="mt-3 mb-1">
        <Col md={2} className="align-middle">
          <div className="pt-3 text-black">
            <span className="d-inline-block fw-bold">
              Appointment Summary
              <i className="bi bi-info-circle ms-1 me-1"></i>
            </span>
            <Dropdown className="d-inline-block action-btn">
              <Dropdown.Toggle id="dropdown-basic" className="action-btn">
                Action
              </Dropdown.Toggle>
              <Dropdown.Menu className="trtmt-shrtcts-dropdown">
                {ACTION_ITEMS.map(({ key, title }, index) => (
                  <Dropdown.Item href="#" key={`${index}-${key}`}>
                    {title}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Col>
        <Col md={2} className="colDDFilter">
          <Form.Group className="mb-1">
            <Form.Label className="label-top text-black text-no-design">
              Date Range
            </Form.Label>
            <InputGroup>
              <InputGroup.Text className="br-0">
                <i className="bi bi-calendar"></i>
              </InputGroup.Text>
              <Form.Control type="text" placeholder="10/17/2023-10/21/2023" />
            </InputGroup>
          </Form.Group>
        </Col>
        <Col md={2} className="colDDFilter">
          <Form.Group className="mb-1">
            <Form.Label className="label-top text-black text-no-design">
              Location
            </Form.Label>
            <InputGroup>
              <Form.Select
                value={selectedLoc}
                onChange={(e) => setSelectedLoc(e.target.value)}
              >
                {locationOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
          </Form.Group>
        </Col>
        <Col md={6} className="text-end">
          <Button variant="light" className="btn-ct">
            <i className="fa fa-file-export me-1"></i>
            Export
          </Button>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <DashboardTable
            title="Scorecards"
            columns={columns}
            data={filteredData}
            allowMultiSorting={false}
            allowSorting={false}
            shouldShowFooter={true}
            footerData={footerColumns}
            classNameTable="score-card-tbl"
          />
        </Col>
      </Row>
    </>
  )
}

export default LocationTab
